/**
 * The single owner of post-meeting speaker-suggestion confirm/reject.
 *
 * Before the unified speaker model there were two hand-rolled copies of this
 * logic — one inline in `RecordingControls` (live focus view) and one in the
 * `SpeakerHypothesisPills` smart wrapper — and NEITHER invalidated the meeting
 * / transcript caches, so confirming an AI name suggestion after a meeting did
 * nothing visible (defect 1/2 in the plan). This hook unifies them: it wraps
 * `applySpeakerHypothesis`, tracks per-action pending keys, and on success
 * cross-invalidates every cache that renders a speaker name so the rename
 * propagates to all open surfaces at once.
 *
 * `pending` keys are `${label}|${name}|${action}` — the same shape both former
 * implementations used, so hosts can keep their existing per-pill spinners.
 */

import { notifications } from "@mantine/notifications"
import { useQueryClient } from "@tanstack/react-query"
import { useCallback, useState } from "react"
import { applySpeakerHypothesis } from "../lib/api"
import { extractErrorMessage } from "../lib/api/errors"
import { queryKeys } from "../lib/query-keys"
import { applySpeakerOptimistic } from "../lib/speaker-optimistic"
import type { IMeetingDetail } from "../lib/types/meeting"
import { useAuth } from "./useAuth"
import { useCompanyApi } from "./useCompanyApi"

export interface ISpeakerHypothesisActions {
  /** In-flight action keys, `${label}|${name}|${action}` — drive per-pill spinners. */
  pending: Set<string>
  /**
   * Confirm or reject an AI name suggestion for a diarisation `label`. Resolves
   * `true` on success, `false` on a handled error (or when no company is
   * active). Errors surface as a normalized danger notification, never a thrown
   * value the host must catch. The meeting query's `speakers[]` is patched
   * optimistically so the name does not flicker while the refetch lands.
   */
  onAction: (label: string, name: string, action: "confirm" | "reject") => Promise<boolean>
}

export function useSpeakerHypothesisActions(meetingId: string): ISpeakerHypothesisActions {
  const company = useCompanyApi()
  const { user } = useAuth()
  const orgId = user?.activeOrganizationId ?? null
  const queryClient = useQueryClient()
  const [pending, setPending] = useState<Set<string>>(new Set())

  const onAction = useCallback(
    async (label: string, name: string, action: "confirm" | "reject"): Promise<boolean> => {
      if (!company) return false
      const key = `${label}|${name}|${action}`
      setPending((prev) => new Set(prev).add(key))
      const meetingKey = queryKeys.meeting(orgId, meetingId)
      const previous = queryClient.getQueryData<IMeetingDetail>(meetingKey)
      if (previous?.speakers) {
        queryClient.setQueryData<IMeetingDetail>(meetingKey, {
          ...previous,
          speakers: applySpeakerOptimistic(
            previous.speakers,
            action === "confirm" ? { kind: "confirm", label, name } : { kind: "reject-suggestion", label },
          ),
        })
      }
      try {
        await applySpeakerHypothesis(company, meetingId, { label, name, action })
        // A confirm renames the speaker; a reject prunes the suggestion. Either
        // way every surface that paints this speaker's name must refresh — the
        // detail page (meeting), the full transcript, and the list card that
        // shows the roster. Cross-invalidating here is the whole point of the
        // unified owner: no host has to remember to do it.
        queryClient.invalidateQueries({ queryKey: meetingKey })
        queryClient.invalidateQueries({ queryKey: queryKeys.transcript(orgId, meetingId) })
        queryClient.invalidateQueries({ queryKey: queryKeys.meetings(orgId) })
        return true
      } catch (err) {
        if (previous !== undefined) queryClient.setQueryData(meetingKey, previous)
        notifications.show({ message: extractErrorMessage(err), color: "danger" })
        return false
      } finally {
        setPending((prev) => {
          const next = new Set(prev)
          next.delete(key)
          return next
        })
      }
    },
    [company, meetingId, orgId, queryClient],
  )

  return { pending, onAction }
}
